import { useState, useEffect, useRef, forwardRef, type HTMLAttributes } from "react";

/**
 * CircularGallery — a 3D carousel of cards arranged on a ring, rotated by page
 * scroll (and gently auto-rotating when idle). Ported to Done-it conventions:
 * DS tokens instead of shadcn ones (surface / line / ink), no `@/` alias, no
 * `dark:` variants (theming is via CSS vars), reduced-motion aware.
 */

function cn(...classes: (string | undefined | null | false)[]) {
  return classes.filter(Boolean).join(" ");
}

export interface GalleryItem {
  title: string;
  subtitle: string;
  image: {
    url: string;
    alt: string;
    pos?: string;
  };
}

interface CircularGalleryProps extends HTMLAttributes<HTMLDivElement> {
  items: GalleryItem[];
  /** How far the cards sit from the centre (px). */
  radius?: number;
  /** Auto-rotation speed (deg/frame) when the user isn't scrolling. */
  autoRotateSpeed?: number;
}

export const CircularGallery = forwardRef<HTMLDivElement, CircularGalleryProps>(
  ({ items, className, radius = 600, autoRotateSpeed = 0.02, ...props }, ref) => {
    const [rotation, setRotation] = useState(0);
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const animationFrameRef = useRef<number | null>(null);

    // Scroll-driven rotation: map page scroll progress to a full 360° turn.
    useEffect(() => {
      const handleScroll = () => {
        setIsScrolling(true);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

        const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrollProgress = scrollableHeight > 0 ? window.scrollY / scrollableHeight : 0;
        setRotation(scrollProgress * 360);

        scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 150);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        window.removeEventListener("scroll", handleScroll);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      };
    }, []);

    // Gentle auto-rotation while idle — skipped when the user prefers reduced motion.
    useEffect(() => {
      const reduce = typeof window !== "undefined"
        && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduce) return;

      const autoRotate = () => {
        if (!isScrolling) setRotation((prev) => prev + autoRotateSpeed);
        animationFrameRef.current = requestAnimationFrame(autoRotate);
      };
      animationFrameRef.current = requestAnimationFrame(autoRotate);

      return () => {
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      };
    }, [isScrolling, autoRotateSpeed]);

    const anglePerItem = 360 / items.length;

    return (
      <div
        ref={ref}
        role="region"
        aria-label="Circular 3D gallery"
        className={cn("relative flex h-full w-full items-center justify-center", className)}
        style={{ perspective: "2000px" }}
        {...props}
      >
        <div
          className="relative h-full w-full"
          style={{ transform: `rotateY(${rotation}deg)`, transformStyle: "preserve-3d" }}
        >
          {items.map((item, i) => {
            const itemAngle = i * anglePerItem;
            const relativeAngle = (itemAngle + (rotation % 360) + 360) % 360;
            const normalizedAngle = Math.abs(relativeAngle > 180 ? 360 - relativeAngle : relativeAngle);
            const opacity = Math.max(0.3, 1 - normalizedAngle / 180);

            return (
              <div
                key={item.image.url}
                role="group"
                aria-label={item.title}
                className="absolute h-[400px] w-[300px]"
                style={{
                  transform: `rotateY(${itemAngle}deg) translateZ(${radius}px)`,
                  left: "50%",
                  top: "50%",
                  marginLeft: "-150px",
                  marginTop: "-200px",
                  opacity,
                  transition: "opacity 0.3s linear",
                }}
              >
                <div className="group relative h-full w-full overflow-hidden rounded-3xl border-4 border-white bg-bg-subtle shadow-[0_20px_50px_-12px_rgba(15,23,42,0.35)]">
                  <img
                    src={item.image.url}
                    alt={item.image.alt}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ objectPosition: item.image.pos || "center" }}
                  />
                  <div className="absolute inset-x-2 bottom-2 rounded-xl border border-line bg-white/90 p-3 font-ds shadow-sm backdrop-blur-md">
                    <h3 className="font-display text-lg font-bold leading-tight tracking-tight text-ink">{item.title}</h3>
                    <p className="mt-0.5 text-[13px] leading-snug text-ink-soft">{item.subtitle}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);

CircularGallery.displayName = "CircularGallery";
